import { useAuth } from '../contexts/AuthContext';

/**
 * FIX (2026-08-31, Jaime bug — Casa Tajalí vs TH1 cross-contamination):
 * effective active unit id for the Home+ modules (Compras/Tareas/Vehículos/
 * Mascotas/Presupuesto).
 *
 * Previously the home-module list requests sent NO explicit unit id and relied
 * on the backend requireUnit auto-resolver. That resolver mis-resolves a user
 * who both owns a standalone "Hogar" unit (e.g. Casa Tajalí) and belongs to a
 * condo unit (e.g. TH1), so items added in one appeared in the other's list.
 *
 * Resolution order mirrors the UI context switcher (AppShell/MobileMenu):
 *   1. Active owned unit (standalone or condo-linked "Hogar" selection).
 *   2. Otherwise, the unit attached to the active condo membership
 *      (resident of a condo unit — e.g. TH1).
 *   3. Otherwise undefined → backend auto-resolve (unchanged behavior).
 *
 * The returned id must be passed to every homeApi list/create/update call and
 * included in the fetch callbacks' dependency arrays so switching units
 * triggers an immediate refetch with the new unit scoping (and the axios
 * X-Active-Unit-Id header is only a fallback, not the primary signal).
 */
export function useActiveHomeUnitId(): string | undefined {
  const { activeOwnedUnitId, activeCondoId, roles } = useAuth();
  if (activeOwnedUnitId) return activeOwnedUnitId;
  if (activeCondoId) {
    const membership = (roles || []).find((r) => r.condoId === activeCondoId);
    if (membership?.unitId) return membership.unitId;
  }
  // FIX (2026-09-22, standalone-home resident members): a resident/familiar
  // invited into someone else's standalone unit has a membership row with
  // condo_id NULL + unit_id set (no condo context to select). She is not the
  // owner, so activeOwnedUnitId is undefined and the condo branch above never
  // matches; without this fallback her home-module requests auto-resolved
  // elsewhere or sent no unit at all. Only pick when exactly one standalone
  // membership unit exists (mirrors the backend single-unit auto-resolve rule).
  const standaloneMemberships = (roles || []).filter(
    (r) => r.unitId && (r.condoId === null || r.condoId === undefined)
  );
  if (standaloneMemberships.length === 1) return standaloneMemberships[0].unitId;
  return undefined;
}

export default useActiveHomeUnitId;
